import PropTypes from 'prop-types';
import { useEffect, useContext } from 'react';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import joinName from "./name.mjs";
import useUpdateEffect from "./useUpdateEffect.mjs";
import { IdentityContext } from "./context.mjs";
/**
 * You may be asking yourself, "why do we need this type definition?
 * Isn't string inferred from gql?" Yes, you would think so! Yet, under
 * certain situations (such as github ci) which we have not yet figured
 * out, TypeScript parses the return of gql as `any`.
 * @type {string}
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const GET_ACCOUNT_QUERY = gql`
  query CurrentAccount {
    account {
      uuid
      email
      firstName
      lastName
      createdAt
    },
    user {
      uuid
      email
      createdAt
    }
  }
`;

/**
 * React provider component for retrieving the user's information
 * after login and making it available in downstream components.
 * This component must be placed *inside* the Authentication and GraphQL
 * providers.
 *
 * @param {object} props
 * @param {Function} props.onChange
 * @param {import('react').ReactNode|Function} props.children
 * @component
 */
function IdentityProviderInternal({
  onChange,
  children
}) {
  const {
    authenticated,
    user: {
      accountUuid: authAccountUuid,
      userUuid: authUserUuid
    }
  } = useAuthentication();
  const authReady = !!authenticated && !!authAccountUuid && !!authUserUuid;
  const {
    loading,
    data: {
      account = null,
      user = null
    } = {},
    refetch
  } = useQuery(GET_ACCOUNT_QUERY, {
    // If an error occurs, we simply don't populate the account information.
    // The error will get logged by app-com-graphql
    noThrow: true,
    useCache: false,
    skip: !authReady
  });

  // fetch user info once authenticated or if login changes
  useEffect(() => {
    if (authReady) refetch();
  }, [refetch, authReady, authAccountUuid, authUserUuid]);
  const {
    uuid: accountUuid,
    email: accountEmail,
    firstName,
    lastName,
    createdAt
  } = account || {};
  const {
    uuid: userUuid,
    email: userEmail
  } = user || {};
  const identified = !!(authenticated && accountUuid && userUuid);

  /** @type {import('./context').Identity} */
  const identity = {
    accountUuid,
    accountEmail,
    userUuid,
    email: userEmail || accountEmail,
    firstName,
    lastName,
    name: identified ? joinName(account?.firstName, account?.lastName) : '',
    createdAt: identified && createdAt ? new Date(createdAt) : null
  };

  // Trigger the change event only when user info changes
  useUpdateEffect(() => {
    if (onChange) onChange(authenticated && identified && identity || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, authenticated, accountUuid, userUuid, identity.name]);
  const context = /** @type {import('./context').IdentityContextObject} */useMemoObject({
    loading,
    authenticated,
    identified,
    ...(identified && !loading ? identity : {}),
    user: identified && !loading ? identity : {}
  });

  // if we receive a function, execute it and pass in the client
  if (typeof children === 'function') {
    // eslint-disable-next-line no-param-reassign
    children = /** @type {import('react').ReactNode} */children(context);
  }
  return /*#__PURE__*/_jsx(IdentityContext.Provider, {
    value: context,
    children: children
  });
}
IdentityProviderInternal.propTypes = {
  onChange: PropTypes.func
};

/**
 * Identity context provider and state management component,
 * with fallthrough to a parent context provider if available,
 * as we do not need to load identity if it's already present.
 *
 * @param {object} props
 * @param {Function} props.onChange
 * @param {import('react').ReactNode|Function} props.children
 * @component
 */
export function IdentityProvider(props) {
  const existingContext = useContext(IdentityContext);
  const {
    children
  } = props;

  // if the context already exists on the page defer to it
  if (existingContext) {
    if (typeof children === 'function') {
      return children(existingContext);
    }
    return children;
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return /*#__PURE__*/_jsx(IdentityProviderInternal, {
    ...props
  });
}
IdentityProvider.propTypes = IdentityProviderInternal.propTypes;